import { useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';

/** Hooks */
import useResponsive from 'hooks/useResponsive';
import useAuth from 'hooks/useAuth';

/** Components */
import BrandCardGlideCarousel from 'components/Carousels/core/BrandCardGlideCarousel';
import ProductCarouselsSkeleton from 'components/Carousels/skeleton/ProductCarouselsSkeleton';

/** Utils */
import { getBrands } from 'api/brand';
import { FLAIR_ID_HIGH_SELL_THROUGH } from 'constants/constants';

const MAX_PRODUCTS = 16;

interface HighSellThroughSectionProps {
  carouselSettings: Record<string, unknown>;
  page?: string;
}

const Title = () => {
  return (
    <Typography variant="h6" fontWeight="700" data-testid="brands-high-sell-through-title">
      Brands on repeat
    </Typography>
  );
};

function useHighSellThroughBrands(shippingCountryCode?: string) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchBrands = async () => {
      setIsLoading(true);
      try {
        const response = await getBrands({
          limit: MAX_PRODUCTS,
          offset: 0,
          shippingCountryCode,
          flairs: [FLAIR_ID_HIGH_SELL_THROUGH],
        });
        setData(response);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrands();
  }, [shippingCountryCode]);

  return { data, error, isLoading };
}

const HighSellThroughSection = ({ carouselSettings, page = 'for-you' }: HighSellThroughSectionProps) => {
  const { user, isAuthenticated } = useAuth();
  const isMobile = useResponsive('down', 'sm');
  const shippingCountryCode = user?.shippingCountry;
  const { data, error, isLoading } = useHighSellThroughBrands(shippingCountryCode);

  if (isLoading) {
    return <ProductCarouselsSkeleton page={page} isAuthenticated={isAuthenticated} />;
  }

  if (error || !data?.brands || data.brands.length < 4) return null;

  return (
    <div style={{ backgroundColor: '#FFDCC71F', paddingBottom: '1rem' }}>
      <Container
        maxWidth="xl"
        disableGutters={isMobile}
        sx={
          {
            paddingTop: isMobile ? '15px' : '29px',
            paddingBottom: isMobile ? '15px' : '29px',
          } as SxProps<Theme>
        }
        data-testid="brands-high-sell-through-container"
      >
        <BrandCardGlideCarousel
          sx={{ mt: isMobile ? 1 : 0 } as SxProps<Theme>}
          brands={data.brands}
          isLoading={isLoading}
          title={<Title />}
          carouselSettings={carouselSettings}
          viewMoreUrl="/for-you/high-sell-through?tab=brands&reference=highsellthrough"
          showMoreUrl={page === 'for-you'}
          data-testid="brands-high-sell-through-carousel"
        />
      </Container>
    </div>
  );
};

export default HighSellThroughSection;
